import { isWorkspaceRowEligible, workspaceCompanyId } from "./companyEligibility";
const CSV_EXTENSION = '.csv';
const DEFAULT_BASENAME = 'companies-export';
const MAX_FILENAME_LENGTH = 120;
const RESERVED_FILENAME_CHARACTERS = /[<>:"/\\|?*]/g;
const SEARCH_EXPORT_LIMIT = 1000;
const SEARCH_TOTAL_SENTINEL = SEARCH_EXPORT_LIMIT + 1;

function localDateStamp(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultExportBasename(date = new Date()) {
  return `alphasearch-export-${localDateStamp(date)}`;
}

function withoutControlCharacters(value) {
  return Array.from(value)
    .map((character) => {
      const codePoint = character.codePointAt(0);
      return codePoint < 32 || codePoint === 127 ? '_' : character;
    })
    .join('');
}

function withoutTrailingCsvExtensions(value) {
  return value
    .trim()
    .replace(/[. ]+$/g, '')
    .replace(/(?:\.csv)+$/gi, '')
    .replace(/[. ]+$/g, '');
}

export function sanitizeCsvBasename(value, fallback = DEFAULT_BASENAME) {
  const rawValue = String(value ?? '').normalize('NFKC');
  const withoutExtensions = withoutTrailingCsvExtensions(rawValue);
  const sanitized = withoutControlCharacters(withoutExtensions)
    .replace(RESERVED_FILENAME_CHARACTERS, '_')
    .replace(/^\.+/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[. ]+$/g, '')
    .replace(/_+/g, '_');
  const safeFallback = withoutTrailingCsvExtensions(String(fallback || DEFAULT_BASENAME))
    || DEFAULT_BASENAME;
  return sanitized || safeFallback;
}

export function csvFilename(value, fallback, date = new Date()) {
  const fallbackBasename = fallback || defaultExportBasename(date);
  const maxBasenameLength = MAX_FILENAME_LENGTH - CSV_EXTENSION.length;
  const basename = sanitizeCsvBasename(value, fallbackBasename)
    .slice(0, maxBasenameLength)
    .replace(/[. ]+$/g, '') || defaultExportBasename(date);
  return `${basename}${CSV_EXTENSION}`;
}

export function suggestedCsvFilename(exportType, label, date = new Date()) {
  const fallbackByType = {
    chat: 'chat',
    search: 'search',
    workspace: 'workspace',
  };
  const source = String(label ?? '').trim()
    ? sanitizeCsvBasename(label)
    : '';
  const sourceStem = source || fallbackByType[exportType] || 'alphasearch';
  const suffix = `-companies-${localDateStamp(date)}`;
  const maxSourceLength = MAX_FILENAME_LENGTH - CSV_EXTENSION.length - suffix.length;
  const cappedSource = sourceStem
    .slice(0, maxSourceLength)
    .replace(/[. ]+$/g, '') || 'alphasearch';
  return csvFilename(`${cappedSource}${suffix}`, undefined, date);
}

export function buildExportReviewModel({
  exportType,
  sourceLabel,
  scopeLabel,
  companyCount,
  expectedRowCount,
  countKind = 'unavailable',
  warnings = [],
  suggestedFilename,
  date = new Date(),
}) {
  return {
    exportType,
    sourceLabel,
    scopeLabel,
    companyCount: Number.isFinite(companyCount) ? companyCount : null,
    expectedRowCount: Number.isFinite(expectedRowCount) ? expectedRowCount : null,
    countKind: ['known', 'estimated'].includes(countKind) ? countKind : 'unavailable',
    warnings: Array.isArray(warnings) ? warnings.filter(Boolean) : [],
    suggestedFilename: csvFilename(
      suggestedFilename || suggestedCsvFilename(exportType, sourceLabel, date),
      undefined,
      date
    ),
  };
}

export function buildSearchExportReview({ selectedCount, totalRows, title, date = new Date() }) {
  const isSelectedExport = selectedCount > 0;
  const isUnknownLargeTotal = !isSelectedExport && totalRows === SEARCH_TOTAL_SENTINEL;
  const expectedCount = isSelectedExport
    ? selectedCount
    : Math.min(totalRows, SEARCH_EXPORT_LIMIT);
  return buildExportReviewModel({
    exportType: 'search',
    sourceLabel: 'Company Search',
    scopeLabel: isSelectedExport
      ? `${selectedCount.toLocaleString()} selected companies`
      : `Current search, up to ${SEARCH_EXPORT_LIMIT.toLocaleString()} companies`,
    companyCount: isSelectedExport
      ? selectedCount
      : (isUnknownLargeTotal ? null : totalRows),
    expectedRowCount: expectedCount || null,
    countKind: isSelectedExport ? 'known' : 'estimated',
    warnings: !isSelectedExport && totalRows > SEARCH_EXPORT_LIMIT
      ? [{
        code: 'search_export_limit',
        message: `Only the first ${SEARCH_EXPORT_LIMIT.toLocaleString()} matching companies will be exported.`,
      }]
      : [],
    suggestedFilename: suggestedCsvFilename('search', title, date),
    date,
  });
}

export function buildWorkspaceExportReview({
  name,
  companyCount,
  selectedCount = null,
  date = new Date(),
}) {
  const isSelectedExport = Number.isFinite(selectedCount);
  const reviewedCount = isSelectedExport ? selectedCount : companyCount;
  return buildExportReviewModel({
    exportType: 'workspace',
    sourceLabel: name || 'Workspace',
    scopeLabel: isSelectedExport
      ? `${selectedCount.toLocaleString()} selected ${selectedCount === 1 ? 'company' : 'companies'}`
      : 'All workspace companies',
    companyCount: reviewedCount,
    expectedRowCount: reviewedCount,
    countKind: Number.isFinite(reviewedCount) ? 'known' : 'unavailable',
    suggestedFilename: suggestedCsvFilename('workspace', name, date),
    date,
  });
}

export function workspaceRowIds(rows = []) {
  return rows.filter(isWorkspaceRowEligible).map(workspaceCompanyId);
}

export function validWorkspaceCompanyIds(selectedIds = [], rows = []) {
  const eligible = new Set(workspaceRowIds(rows));
  return selectedIds.map(String).filter((id) => eligible.has(id));
}

export async function collectWorkspaceCompanyIds(workspaceId, fetchPage) {
  const pageSize = 500;
  const firstResponse = await fetchPage(workspaceId, 1, pageSize);
  if (!firstResponse || firstResponse.status !== 200) return null;

  const firstRows = firstResponse.data.rows || [];
  const firstPreviousRows = firstResponse.data.previously_enriched_rows || [];
  const companyIds = [
    ...workspaceRowIds(firstRows),
    ...workspaceRowIds(firstPreviousRows),
  ];
  let scannedRows = firstRows.length;
  let scannedPreviousRows = firstPreviousRows.length;
  const totalCompanies = firstResponse.data.company_count || firstRows.length;
  const totalPreviousCompanies = firstResponse.data.previously_enriched_company_count
    ?? firstPreviousRows.length;
  let page = 1;
  while (scannedRows < totalCompanies || scannedPreviousRows < totalPreviousCompanies) {
    page += 1;
    const response = await fetchPage(workspaceId, page, pageSize);
    if (!response || response.status !== 200) return null;
    const rows = response.data?.rows || [];
    const previousRows = response.data?.previously_enriched_rows || [];
    if (scannedRows < totalCompanies && rows.length === 0) return null;
    if (scannedPreviousRows < totalPreviousCompanies && previousRows.length === 0) return null;
    scannedRows += rows.length;
    scannedPreviousRows += previousRows.length;
    companyIds.push(...workspaceRowIds(rows), ...workspaceRowIds(previousRows));
  }
  return [...new Set(companyIds)];
}

export function downloadCsvBlob(data, filename, date = new Date()) {
  const blob = data instanceof Blob ? data : new Blob([data], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = csvFilename(filename, undefined, date);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
